import _ from 'lodash'
import Guard from 'arg-guard'

import Logger from '@/logger'

import LocalDatabase from '../local/local-database'

const loggingCategory = 'Migration'

/**
 * A base representation of a migration.
 *
 * @export
 * @class Migration
 */
export default class Migration {

    /**
     * Creates an instance of Migration.
     * @param {string} name Migration names must be prefixed with sequential numbers and must be unique.
     * @param {Function} migrationFunction A function that takes an authContext as it's first parameter.
     * @memberof Migration
     */
    constructor(name, migrationFunction) {
        Guard.throwIf(name, 'name')
        Guard.throwIf(migrationFunction, 'migrationFunction')

        if (!_.isFunction(migrationFunction))
            throw new TypeError(`Invalid function for migration ${ name }.`)

        this.name = name
        this.migrate = migrationFunction
        this.db = new LocalDatabase()
    }

    /**
     * Formats a contextual data key to allow multiple users to share the data store.
     *
     * @param {string} key
     * @param {Context} authContext
     * @returns {string} The formatted context key to access the data item.
     * @memberof Migration
     */
    formatContextualKey(key, authContext) {
        return authContext && authContext.type != 'guest'
            ? `${ authContext.id }-${ key }`
            : `guest-${ key }`
    }

    /**
     * Gets the selected data from local storage if there is one.
     *
     * @param {string} key
     * @param {Context} authContext
     * @param {Object} options { table = 'context', ReturnType } = { table: 'context', ReturnType: undefined }
     * @returns {Promise<ReturnType>} The data
     * @memberof RemoteStorage
     */
    async getAsync(key, authContext, { table = 'context', ReturnType } = { table: 'context', ReturnType: undefined }) {
        Guard.throwIf(key, 'key')

        try {
            Logger.log(`MIGRATION [${ this.name }]: Getting ${ key }`, loggingCategory)

            const contextualKey = this.formatContextualKey(key, authContext)
            let value = await this.db[table].getItem(contextualKey)

            if (ReturnType && _.isFunction(ReturnType.fromStoreJson))
                value = ReturnType.fromStoreJson(value)

            Logger.logObject(value, `Data loaded [${ table }].[${ key }]`, 'Local Storage')
        } catch (err) {
            Logger.error(`MIGRATION ERROR: Unable to get '${ key }' from remote storage: ${ err }`, loggingCategory)
            throw err
        }
    }

    /**
     * Removes data from local storage.
     *
     * @param {string} key
     * @param {Context} authContext
     * @param {Object} options { table = 'context' } = { table: 'context' }
     * @returns {Promise<boolean>} The removal response.
     * @memberof RemoteStorage
     */
    async removeAsync(key, authContext, { table = 'context' } = { table: 'context' }) {
        Guard.throwIf(key, 'key')

        try {
            Logger.log(`MIGRATION [${ this.name }]: Removing ${ key }`, loggingCategory)

            const contextualKey = this.formatContextualKey(key, authContext)
            const existingValue = await this.db[table].getItem(contextualKey)

            if (existingValue) {
                await this.db[table].removeItem(key)
                Logger.logObject(key, `Data removed [${ table }].[${ key }]`, 'Local Storage')
                return true
            }

            return false
        } catch (err) {
            Logger.error(`MIGRATION ERROR: Unable to remove '${ key }' from local storage: ${ err }`, loggingCategory)
            throw err
        }
    }

    /**
     * Inserts or updates data to local storage.
     *
     * @param {string} key
     * @param {Context} authContext
     * @param {Object} data The data to upsert.
     * @param {Object} options { table = 'context' } = { table: 'context' }
     * @returns {Promise<Object>} The altered data value used as storage.
     * @memberof RemoteStorage
     */
    async upsertAsync(key, authContext, data, { table = 'context' } = { table: 'context' }) {
        Guard.throwIf(key, 'key')
        Guard.throwIf(data, 'data')

        try {
            Logger.logObject(data, `MIGRATION [${ this.name }]: Upserting ${ key }`)
            if (_.isFunction(data.toStoreJson)) data = data.toStoreJson()

            const contextualKey = this.formatContextualKey(key, authContext)
            const existingValue = await this.db[table].getItem(contextualKey)

            if (_.isEqual(data, existingValue))
                return existingValue

            const value = await this.db[table].setItem(key, data)

            Logger.logObject(value, `Data persisted [${ table }].[${ key }]`, 'Local Storage')
            return value
        } catch (err) {
            Logger.error(`MIGRATION ERROR: Unable to save '${ key }' to local storage: ${ err }`)
            throw err
        }
    }

    /**
     * Executes the provided migration code.
     *
     * @memberof Migration
     * @param {Context} authContext
     */
    async executeAsync(authContext) {
        Logger.log(`Executing state migration [${ this.name }]...`)

        try {
            await this.migrate(authContext)
            Logger.log('Migration applied!')
        } catch (err) {
            Logger.warn('Migration failed!')
            throw err
        }
    }
}
